import { useEffect, useRef, useState } from 'react';

interface ReasonCardProps {
  icon: string;
  title: string;
  description: string;
  index: number;
}

function ReasonCard({ icon, title, description, index }: ReasonCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className="bg-white rounded-lg p-8 border border-gray-100 hover:border-green-200 transition-all duration-300"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.6s ease ${index * 120}ms, transform 0.6s ease ${index * 120}ms`,
      }}
    >
      <div className="w-14 h-14 flex items-center justify-center bg-green-50 text-green-600 rounded-lg mb-5">
        <i className={`${icon} text-2xl`} />
      </div>
      <h4 className="font-bold text-gray-800 text-lg mb-3">{title}</h4>
      <p className="text-gray-500 text-sm leading-relaxed">{description}</p>
    </div>
  );
}

const reasons = [
  {
    icon: 'ri-bill-line',
    title: '明朗会計',
    description:
      '作業を始める前に必ずお見積りをご提示します。内容をご確認・ご納得いただいてから作業を開始しますので、後から料金が変わることはありません。',
  },
  {
    icon: 'ri-car-line',
    title: '代車サービス',
    description:
      '車検や修理でお車をお預かりする際、代車をご用意しています（要予約）。日常の移動にお困りにならないよう配慮しています。',
  },
  {
    icon: 'ri-shield-check-line',
    title: '幅広い車種に対応',
    description:
      '国産車はもちろん、輸入車（ヨーロッパ車・アメ車など）の整備・修理にも対応しています。経験豊富なスタッフが丁寧に対応します。',
  },
];

export default function ReasonsSection() {
  return (
    <section id="reasons" className="py-20" style={{ backgroundColor: '#f8faf8' }}>
      <div className="max-w-6xl mx-auto px-6">
        {/* Heading */}
        <div className="text-center mb-14">
          <span className="text-green-600 text-sm font-semibold tracking-widest uppercase">Why Choose Us</span>
          <h2 className="text-3xl font-bold text-gray-800 mt-2">選ばれる理由</h2>
          <div className="w-12 h-1 bg-green-600 mx-auto mt-4 rounded-full" />
          <p className="text-gray-500 mt-4 text-sm leading-relaxed max-w-xl mx-auto">
            お客様に安心してご利用いただくための、私たちの取り組みをご紹介します。
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {reasons.map((r, i) => (
            <ReasonCard key={r.title} {...r} index={i} />
          ))}
        </div>

        {/* Bottom CTA Banner */}
        <div className="mt-14 bg-green-600 rounded-lg px-8 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-white/80 text-sm mb-1">まずはお気軽にご相談ください</p>
            <p className="text-white font-bold text-xl">お見積りは無料です。お電話またはLINEでお問い合わせください。</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <a
              href="tel:0000000000"
              className="flex items-center gap-2 bg-white text-green-700 font-bold px-6 py-3 rounded-md hover:bg-green-50 transition-colors cursor-pointer whitespace-nowrap"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-fill" />
              </div>
              000-000-0000
            </a>
            <a
              href="https://lin.ee/xxxxxxx"
              target="_blank"
              rel="noopener noreferrer nofollow"
              className="flex items-center gap-2 font-bold px-6 py-3 rounded-md transition-opacity cursor-pointer whitespace-nowrap hover:opacity-90"
              style={{ backgroundColor: '#06C755', color: '#fff' }}
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-line-fill" />
              </div>
              LINEで問い合わせ
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
